import { makeStyles } from "@mui/styles";
import React, { useState } from "react";
import { useAssetSentinelData } from "../AssetSentinel/useAssetSentinelData";
import { Button } from "@mui/material";
import { StatusBadge } from "../../utils/functions";
import AddEditChecklistItemDialog from "./AddEditChecklistItemDialog";

const InspectionSetupTabs = {
	ITEMS: "items",
	TYPES: "types",
};

const Tabs = [
	{
		id: InspectionSetupTabs.ITEMS,
		label: "☑️ Checklist Items",
	},
	{
		id: InspectionSetupTabs.TYPES,
		label: "⚙️ Inspection Types",
	},
];

const useStyles = makeStyles((theme) => ({
	sideMenuTitle: {
		fontSize: 20,
		fontWeight: 700,
		color: theme.palette.text.primary,
		marginBottom: "5px",
	},
	colorTitle: {
		color:
			theme.palette.mode === "dark"
				? "#fff !important"
				: "#000 !important",
	},
}));

const InspectionSetup = () => {
	const classes = useStyles();
	const [clTab, setClTab] = useState(InspectionSetupTabs.ITEMS);
	const { checklistItems, setChecklistItems } = useAssetSentinelData();

	const activeItems = checklistItems.filter((i) => i.isActive !== false);
	const inactiveItems = checklistItems.filter((i) => i.isActive === false);

	const [draggedId, setDraggedId] = useState(null);
	const [dragOverId, setDragOverId] = useState(null);
	const [dragPos, setDragPos] = useState("bottom"); // "top" | "bottom"
	const [openDialog, setOpenDialog] = useState(false);

	const handleDragStart = (e, id) => {
		setDraggedId(id);
		e.dataTransfer.effectAllowed = "move";
		// Transparent drag image so the row itself shows the opacity change
		const ghost = document.createElement("div");
		ghost.style.cssText = "position:absolute;top:-999px;opacity:0;";
		document.body.appendChild(ghost);
		e.dataTransfer.setDragImage(ghost, 0, 0);
		setTimeout(() => document.body.removeChild(ghost), 0);
	};

	const handleDragOver = (e, id) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = "move";
		if (id === draggedId) return;
		// Determine top/bottom half
		const rect = e.currentTarget.getBoundingClientRect();
		const mid = rect.top + rect.height / 2;
		setDragOverId(id);
		setDragPos(e.clientY < mid ? "top" : "bottom");
	};

	const handleDragLeave = () => {
		setDragOverId(null);
	};

	const handleDrop = (e, targetId) => {
		e.preventDefault();
		if (!draggedId || draggedId === targetId) {
			setDraggedId(null);
			setDragOverId(null);
			return;
		}
		setChecklistItems((items) => {
			const arr = [...items];
			const fromIdx = arr.findIndex((i) => i.id === draggedId);
			const toIdx = arr.findIndex((i) => i.id === targetId);
			const [moved] = arr.splice(fromIdx, 1);
			const insertAt =
				dragPos === "top" ? toIdx : toIdx + (fromIdx < toIdx ? 0 : 1);
			arr.splice(
				Math.max(0, insertAt > fromIdx ? insertAt - 1 : insertAt),
				0,
				moved,
			);
			return arr.map((item, i) => ({ ...item, sortOrder: i + 1 }));
		});
		setDraggedId(null);
		setDragOverId(null);
	};

	const handleDragEnd = () => {
		setDraggedId(null);
		setDragOverId(null);
	};

	const DragHandle = () => (
		<div className="cl-drag-handle" title="Drag to reorder">
			<div className="cl-drag-dots">
				{[...Array(6)].map((_, i) => (
					<div key={i} className="cl-drag-dot" />
				))}
			</div>
		</div>
	);

	return (
		<>
			<div>
				<div className={classes.sideMenuTitle}>Inspection History</div>
				{/* ── Tabs ── */}
				<div
					style={{
						display: "flex",
						gap: 0,
						borderBottom: "1px solid var(--border)",
						marginBottom: 20,
					}}
				>
					{Tabs.map((t) => (
						<button
							key={t.id}
							onClick={() => setClTab(t.id)}
							style={{
								padding: "10px 20px",
								fontSize: 16,
								fontWeight: 600,
								border: "none",
								background: "none",
								cursor: "pointer",
								color:
									clTab === t.id
										? "var(--accent)"
										: "var(--text2)",
								borderBottom:
									clTab === t.id
										? "2px solid var(--accent)"
										: "2px solid transparent",
								marginBottom: -1,
								transition: "color 0.15s",
							}}
						>
							{t.label}
						</button>
					))}
				</div>

				{clTab === InspectionSetupTabs.ITEMS && (
					<>
						{/* Stats */}
						<div className="cl-stats">
							<div
								className={`cl-stat ${classes.cardBackground} ${classes.colorTitle}`}
							>
								<div className="cl-stat-num">
									{checklistItems.length}
								</div>
								<div className="cl-stat-label">Total Items</div>
							</div>
							<div
								className={`cl-stat ${classes.cardBackground} ${classes.colorTitle}`}
							>
								<div
									className="cl-stat-num"
									style={{ color: "var(--green)" }}
								>
									{activeItems.length}
								</div>
								<div className="cl-stat-label">Active</div>
							</div>
							<div
								className={`cl-stat ${classes.cardBackground} ${classes.colorTitle}`}
							>
								<div
									className="cl-stat-num"
									style={{ color: "var(--text3)" }}
								>
									{inactiveItems.length}
								</div>
								<div className="cl-stat-label">Disabled</div>
							</div>
						</div>
						<div className="section-actions mb-2">
							<Button
								variant="contained"
								color="primary"
								onClick={() => setOpenDialog(true)}
							>
								+ Add Checklist Item
							</Button>
						</div>

						{/* Active items table */}
						<div
							className={`section-header ${classes.colorTitle}`}
							style={{ marginBottom: 12 }}
						>
							<div className="section-title">
								Active Items ({activeItems.length})
							</div>
							<div style={{ fontSize: 12 }}>
								Drag <span style={{ fontWeight: 700 }}>⠿</span>{" "}
								handles to reorder · order applies to all future
								inspections
							</div>
						</div>

						<div
							className="table-wrap"
							style={{ marginBottom: 24 }}
						>
							{activeItems.length === 0 && (
								<div className="empty-state">
									<div className="empty-icon">☑️</div>
									<div className={classes.colorTitle}>
										No active checklist items
									</div>
								</div>
							)}
							{activeItems.map((item, idx) => {
								const isDragging = draggedId === item.id;
								const isOver = dragOverId === item.id;
								const rowClass = [
									"cl-item-row",
									isDragging ? "dragging" : "",
									isOver && dragPos === "top"
										? "drag-over-top"
										: "",
									isOver && dragPos === "bottom"
										? "drag-over-bottom"
										: "",
								]
									.filter(Boolean)
									.join(" ");

								return (
									<div
										key={item.id}
										className={`${rowClass} ${classes.colorTitle}`}
										draggable
										onDragStart={(e) =>
											handleDragStart(e, item.id)
										}
										onDragOver={(e) =>
											handleDragOver(e, item.id)
										}
										onDragLeave={handleDragLeave}
										onDrop={(e) => handleDrop(e, item.id)}
										onDragEnd={handleDragEnd}
									>
										<DragHandle />
										<div className="cl-item-left">
											<span className="cl-sort-badge">
												{idx + 1}
											</span>
											<div className="cl-item-text">
												<div className="cl-item-name">
													{item.name}
												</div>
												{item.description && (
													<div className="cl-item-desc">
														{item.description}
													</div>
												)}
												<div
													style={{
														display: "flex",
														flexWrap: "wrap",
														gap: 4,
														marginTop: 4,
													}}
												>
													{!item.inspectionTypes ||
													item.inspectionTypes
														.length === 0 ? (
														<span
															style={{
																fontSize: 10,
																color: "var(--text3)",
																fontStyle:
																	"italic",
															}}
														>
															All inspection types
														</span>
													) : (
														item.inspectionTypes.map(
															(t) => (
																<span
																	key={t}
																	style={{
																		fontSize: 10,
																		fontWeight: 600,
																		padding:
																			"1px 7px",
																		borderRadius: 20,
																		background:
																			"rgba(245,166,35,0.12)",
																		color: "var(--accent)",
																		border: "1px solid rgba(245,166,35,0.3)",
																	}}
																>
																	{t}
																</span>
															),
														)
													)}
												</div>
											</div>
										</div>
										<div
											style={{
												display: "flex",
												alignItems: "center",
											}}
										>
											<StatusBadge status="Active" />
										</div>
										<div className="cl-item-actions">
											<button
												className={`btn btn-ghost btn-sm ${classes.colorTitle}`}
												// onClick={() => {
												// 	setEditItem(item);
												// 	setEditForm({
												// 		name: item.name,
												// 		description:
												// 			item.description || "",
												// 		inspectionTypes:
												// 			item.inspectionTypes ||
												// 			[],
												// 	});
												// }}
											>
												Edit
											</button>
											<button
												className={`btn btn-ghost btn-sm ${classes.colorTitle}`}
												// onClick={() =>
												// 	handleToggleActive(item.id)
												// }
											>
												Disable
											</button>
											<button
												className={`btn btn-danger btn-sm ${classes.colorTitle}`}
												// onClick={() =>
												// 	setConfirmDelete(item)
												// }
											>
												Remove
											</button>
										</div>
									</div>
								);
							})}
						</div>

						{/* Disabled items */}
						{inactiveItems.length > 0 && (
							<>
								<div
									className={`section-header ${classes.colorTitle}`}
									style={{ marginBottom: 12 }}
								>
									<div
										className="section-title"
										style={{
											fontSize: 13,
										}}
									>
										Disabled Items ({inactiveItems.length})
									</div>
									<div
										style={{
											fontSize: 12,
										}}
									>
										Not included in new inspections — can be
										re-enabled
									</div>
								</div>
								<div className="table-wrap">
									{inactiveItems.map((item) => (
										<div
											key={item.id}
											className="cl-item-row inactive"
										>
											<div style={{ width: 28 }} />
											<div className="cl-item-left">
												<span
													className={`cl-sort-badge ${classes.colorTitle}`}
												>
													—
												</span>
												<div
													className={`cl-item-text ${classes.colorTitle}`}
												>
													<div className="cl-item-name">
														{item.name}
													</div>
													{item.description && (
														<div className="cl-item-desc">
															{item.description}
														</div>
													)}
												</div>
											</div>
											<div>
												<StatusBadge status="Inactive" />
											</div>
											<div className="cl-item-actions">
												<button
													className={`btn btn-ghost btn-sm ${classes.colorTitle}`}
													// onClick={() =>
													// 	handleToggleActive(item.id)
													// }
												>
													Re-enable
												</button>
												<button
													className={`btn btn-danger btn-sm ${classes.colorTitle}`}
													// onClick={() =>
													// 	setConfirmDelete(item)
													// }
												>
													Remove
												</button>
											</div>
										</div>
									))}
								</div>
							</>
						)}
					</>
				)}
			</div>
			<AddEditChecklistItemDialog
				open={openDialog}
				onClose={() => setOpenDialog(false)}
				onSave={() => undefined}
				// initialData={selectedItem}
				viewOnly={false}
				inspectionTypeOptions={[]}
			/>
		</>
	);
};

export default InspectionSetup;
